import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Users,
  UserPlus,
  Search,
  UserPen,
  UserMinus,
  Mail,
  MapPin,
  ShieldCheck,
  Building2,
  AlertCircle
} from 'lucide-react';
import BotonAcceso from "../elementos/BotonAcceso";
import { useTheme } from "../guardarToken/themeContext";

const apiUrlUsers = process.env.REACT_APP_API_USERS;

function AbmUsuarios() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [usuarioAEliminar, setUsuarioAEliminar] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUsers();
  }, []);

  const getUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(apiUrlUsers);
      setUsuarios(res.data);
    } catch (error) {
      console.error("Error al intentar mostrar usuarios:", error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteUsuario = (usuario) => {
    setUsuarioAEliminar(usuario);
    setOpenDialog(true);
  };

  const deleteUsuario = async () => {
    try {
      await axios.delete(`${apiUrlUsers}${usuarioAEliminar.id}`);
      setOpenDialog(false);
      setUsuarioAEliminar(null);
      getUsers();
    } catch (error) {
      console.error("Error al intentar eliminar el usuario:", error);
    }
  };

  const filteredUsuarios = usuarios.filter((usuario) => {
    const search = searchTerm.toLowerCase();
    return (
      usuario.usuario?.toLowerCase().includes(search) ||
      usuario.nombreuser?.toLowerCase().includes(search) ||
      usuario.apellido?.toLowerCase().includes(search) ||
      usuario.ubicacion?.toLowerCase().includes(search) ||
      usuario.tipo?.toLowerCase().includes(search) ||
      usuario.sector?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className={`flex justify-center items-center h-screen ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
        <CircularProgress color="warning" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-3 lg:p-4 font-sans transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-800"}`}>

      {/* HEADER PREMIUM */}
      <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm p-5 mb-6 flex flex-col md:flex-row items-center justify-between border ring-1 ring-black/5 animate-in slide-in-from-top duration-700`}>
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-3 rounded-xl shadow-lg shadow-indigo-900/20">
            <Users className="text-white w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight leading-none mb-1">Gestión de Usuarios</h1>
            <p className={`text-[11px] ${isDarkMode ? "text-slate-400" : "text-slate-500"} font-black uppercase tracking-wider`}>Administra el personal y sus permisos</p>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          <div className="relative w-48 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar personal..."
              style={{ paddingLeft: '48px' }}
              className={`w-full pr-4 py-2 rounded-xl text-xs font-bold focus:outline-none transition-all ${isDarkMode ? "bg-slate-950 border-slate-800 text-white focus:border-indigo-500/50" : "bg-slate-50 border-slate-200 text-slate-800 focus:border-indigo-500"
                } border`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <BotonAcceso
            variant="accent"
            onClick={() => navigate('/admin/CreateUser')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-black text-[11px] uppercase tracking-widest px-5 py-2.5 rounded-xl shadow-lg shadow-indigo-950/20 flex items-center gap-2 transform hover:scale-[1.02] transition-all"
          >
            <UserPlus className="w-4 h-4" /> Nuevo Usuario
          </BotonAcceso>
        </div>
      </div>

      {/* TABLE AREA */}
      <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm border overflow-hidden animate-in slide-in-from-bottom duration-1000`}>
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left">
            <thead>
              <tr className={`${isDarkMode ? "bg-black/20 text-slate-500 border-slate-800" : "bg-slate-50 text-slate-400 border-slate-100"} border-b text-[10px] font-black uppercase tracking-[0.15em]`}>
                <th className="px-6 py-4">Personal</th>
                <th className="px-6 py-4">Contacto</th>
                <th className="px-6 py-4">Ubicación</th>
                <th className="px-6 py-4">Rol / Permisos</th>
                <th className="px-6 py-4">Sector</th>
                <th className="px-6 py-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? "divide-slate-800" : "divide-slate-100"}`}>
              {filteredUsuarios.map((usuario) => (
                <tr key={usuario.id} className={`group transition-all hover:bg-indigo-500/[0.02]`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-xs ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"
                        }`}>
                        {usuario.nombreuser?.charAt(0)}{usuario.apellido?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-black tracking-tight" style={{ color: isDarkMode ? '#818cf8' : '#0f172a' }}>{usuario.nombreuser} {usuario.apellido}</p>
                        <p className={`text-[10px] font-bold ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>@{usuario.usuario}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                        <Mail className="w-3 h-3 text-indigo-500" />
                        <span className="truncate max-w-[150px]">{usuario.email || 'N/A'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                      <MapPin className="w-3 h-3 text-emerald-500" />
                      {usuario.ubicacion}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${usuario.tipo === 'SuperAdmin' || usuario.tipo === 'Admin'
                      ? (isDarkMode ? "bg-indigo-500/10 text-indigo-400" : "bg-indigo-50 text-indigo-600")
                      : (isDarkMode ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-500")
                      }`}>
                      <ShieldCheck className="w-3 h-3" />
                      {usuario.tipo}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                      <Building2 className="w-3 h-3 text-orange-500" />
                      {usuario.sector}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Tooltip title="Editar Usuario">
                        <button
                          onClick={() => navigate(`/admin/EditUser/${usuario.id}`)}
                          className={`p-2 rounded-xl border transition-all ${isDarkMode
                            ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-indigo-500/50"
                            : "bg-white border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-200 shadow-sm"
                            }`}
                        >
                          <UserPen className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip title="Eliminar Usuario">
                        <button
                          onClick={() => confirmDeleteUsuario(usuario)}
                          className={`p-2 rounded-xl border transition-all ${isDarkMode
                            ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-red-500 hover:border-red-500/50"
                            : "bg-white border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 shadow-sm"
                            }`}
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsuarios.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 grayscale opacity-40">
              <Users className="w-12 h-12 mb-3" />
              <p className="text-xs font-black uppercase tracking-widest text-slate-400 font-sans">No se encontraron usuarios</p>
            </div>
          )}
        </div>
      </div>

      {/* DELETE DIALOG */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: isDarkMode ? '#0f172a' : 'white',
            backgroundImage: 'none',
            p: 1
          }
        }}
      >
        <DialogContent className="flex flex-col items-center py-8 px-10">
          <div className="p-4 rounded-3xl mb-4 bg-red-500/10 text-red-500">
            <AlertCircle className="w-12 h-12" />
          </div>
          <h3 className={`text-lg font-black mb-2 ${isDarkMode ? "text-white" : "text-slate-800"}`}>
            ¿Eliminar Usuario?
          </h3>
          <p className={`text-sm text-center font-medium ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
            Estás por eliminar a <span className="font-bold text-orange-500">{usuarioAEliminar?.nombreuser} {usuarioAEliminar?.apellido}</span>. Esta acción no se puede deshacer.
          </p>
        </DialogContent>
        <DialogActions className="pb-6 px-10 gap-3">
          <button
            onClick={() => setOpenDialog(false)}
            className={`flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${isDarkMode ? "text-slate-400 hover:bg-slate-800" : "text-slate-50"
              }`}
          >
            Cancelar
          </button>
          <button
            onClick={deleteUsuario}
            className="flex-1 py-3 rounded-2xl bg-red-500 text-white hover:bg-red-600 font-black text-xs uppercase tracking-widest shadow-lg shadow-red-950/20 transition-all"
          >
            Confirmar
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AbmUsuarios;
